import React, { useState, useMemo, useEffect } from "react";
import { useGetTeamUsageOverviewQuery } from "../../app/api/console/consoleTeamUsageApiSlice";
import TeamUsageTrendChart from "../../components/TeamUsage/TeamUsageTrendChart";
import TeamUsageMonthlyTable from "../../components/TeamUsage/TeamUsageMonthlyTable";
import BudgetConfigPanel from "../../components/TeamUsage/BudgetConfigPanel";
import AnalyticsWarmingBanner from "../../components/common/AnalyticsWarmingBanner";

const ENV_OPTIONS = [
  { value: "staging", label: "Staging" },
  { value: "production", label: "Production" },
];

const DATE_PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

function toDateStr(d) {
  return d.toISOString().split("T")[0];
}

function TeamUsage() {
  const [selectedEnv, setSelectedEnv] = useState("staging");
  const [datePreset, setDatePreset] = useState(30);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [showBudget, setShowBudget] = useState(false);
  const [pollMs, setPollMs] = useState(0);

  const dateRange = useMemo(() => {
    if (customStart && customEnd) {
      return { startDate: customStart, endDate: customEnd };
    }
    const end = new Date();
    const start = new Date(Date.now() - datePreset * 24 * 60 * 60 * 1000);
    return { startDate: toDateStr(start), endDate: toDateStr(end) };
  }, [datePreset, customStart, customEnd]);

  const selectPreset = (days) => {
    setDatePreset(days);
    setCustomStart("");
    setCustomEnd("");
  };

  const {
    data: usageData,
    isLoading,
    error,
  } = useGetTeamUsageOverviewQuery({
    ...dateRange,
    env: selectedEnv,
  }, { pollingInterval: pollMs });

  const isWarming = !!usageData?.warming;
  useEffect(() => {
    setPollMs(isWarming ? 2000 : 0);
  }, [isWarming]);

  const topCallers = useMemo(() => {
    if (!usageData?.callers || isWarming) return [];
    return usageData.callers.filter((c) => c.total_cost_usd > 0).slice(0, 3);
  }, [usageData, isWarming]);

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-h2 text-text-primary'>Team Usage</h1>
          <p className='text-body-sm text-text-secondary mt-1'>
            Per-person LLM cost attribution and budget management
          </p>
          <p className='text-caption text-text-tertiary mt-1 max-w-2xl'>
            Staging/Production only · known team callers (Secrets Manager + system).
            Staff email aliases roll into the short identity. Eval and other
            callers stay on Cost Analytics.
          </p>
        </div>
      </div>

      {/* Filters first so env/date scope is set before reading headline totals */}
      <div className='flex items-center gap-3 flex-wrap'>
        <div className='flex items-center gap-1 bg-surface-neutral rounded-xl p-0.5'>
          {DATE_PRESETS.map((p) => (
            <button
              key={p.days}
              onClick={() => selectPreset(p.days)}
              className={`px-3 py-2 text-body-xs font-medium rounded-lg transition-all ${
                datePreset === p.days && !customStart
                  ? "bg-white text-text-primary shadow-sm"
                  : "text-text-tertiary hover:text-text-secondary"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className='flex items-center gap-1.5'>
          <input
            type='date'
            value={customStart || dateRange.startDate}
            onChange={(e) => {
              setCustomStart(e.target.value);
              if (!customEnd) setCustomEnd(dateRange.endDate);
            }}
            className='px-2.5 py-1.5 text-body-xs rounded-lg border border-border-subtle bg-white text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-purple'
          />
          <span className='text-body-xs text-text-tertiary'>–</span>
          <input
            type='date'
            value={customEnd || dateRange.endDate}
            onChange={(e) => {
              setCustomEnd(e.target.value);
              if (!customStart) setCustomStart(dateRange.startDate);
            }}
            className='px-2.5 py-1.5 text-body-xs rounded-lg border border-border-subtle bg-white text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-purple'
          />
        </div>

        <div className='w-px h-6 bg-border-subtle' />

        <div className='flex items-center gap-1 bg-surface-neutral rounded-xl p-0.5'>
          {ENV_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSelectedEnv(opt.value)}
              className={`px-3 py-2 text-body-xs font-medium rounded-lg transition-all ${
                selectedEnv === opt.value
                  ? "bg-white text-text-primary shadow-sm"
                  : "text-text-tertiary hover:text-text-secondary"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      {isWarming ? (
        <AnalyticsWarmingBanner
          label='Loading team usage…'
          progress={usageData?.progress || 0}
        />
      ) : (
      <>
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
        <div className='bg-white rounded-2xl border border-border-subtle p-5'>
          <p className='text-body-xs font-medium text-text-secondary mb-1'>
            Team Total (known callers)
          </p>
          <span className='text-metric text-accent-purple'>
            {isLoading ? "..." : `$${usageData?.team_total_cost_usd?.toFixed(2) ?? "0.00"}`}
          </span>
          <p className='text-caption text-text-tertiary mt-1'>
            {dateRange.startDate} – {dateRange.endDate}
          </p>
          {!isLoading && usageData && (usageData.excluded_cost_usd ?? 0) > 0 && (
            <p className='text-caption text-text-tertiary mt-2 leading-snug'>
              Excludes ${usageData.excluded_cost_usd.toFixed(2)} eval/other — see
              reconciliation below.
            </p>
          )}
        </div>
        {topCallers.map((c, i) => (
          <div key={c.caller} className='bg-white rounded-2xl border border-border-subtle p-5'>
            <p className='text-body-xs font-medium text-text-secondary mb-1'>
              #{i + 1} {c.caller}
            </p>
            <span className={`text-metric ${
              i === 0 ? "text-accent-purple" : i === 1 ? "text-blue-500" : "text-accent-green"
            }`}>
              ${c.total_cost_usd.toFixed(2)}
            </span>
            <p className='text-caption text-text-tertiary mt-1'>
              {c.call_count.toLocaleString()} calls
            </p>
          </div>
        ))}
        {topCallers.length < 3 &&
          Array.from({ length: 3 - topCallers.length }).map((_, i) => (
            <div key={`empty-${i}`} className='bg-white rounded-2xl border border-border-subtle p-5'>
              <p className='text-body-xs font-medium text-text-secondary mb-1'>—</p>
              <span className='text-metric text-text-tertiary'>$0.00</span>
            </div>
          ))}
      </div>

      {!isLoading && usageData && (
        <div className='bg-white rounded-2xl border border-border-subtle p-4'>
          <div className='flex items-center justify-between gap-3 flex-wrap mb-3'>
            <p className='text-body-xs font-semibold text-text-primary'>
              Reconcile with Cost Analytics
            </p>
            <p className='text-caption text-text-tertiary'>
              Same env on Cost Analytics = env total below. Cost Analytics All Envs
              also includes Production + Dev stacks.
            </p>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
            <div className='rounded-xl bg-surface-neutral/60 px-3 py-2.5'>
              <p className='text-caption text-text-tertiary mb-0.5'>
                {selectedEnv === "production" ? "Production" : "Staging"} env total
              </p>
              <p className='text-body-sm font-semibold text-text-primary'>
                ${(usageData.env_total_cost_usd ?? usageData.team_total_cost_usd ?? 0).toFixed(2)}
              </p>
              <p className='text-caption text-text-tertiary mt-0.5'>all callers</p>
            </div>
            <div className='rounded-xl bg-surface-neutral/60 px-3 py-2.5'>
              <p className='text-caption text-text-tertiary mb-0.5'>Team (known callers)</p>
              <p className='text-body-sm font-semibold text-accent-purple'>
                ${(usageData.team_total_cost_usd ?? 0).toFixed(2)}
              </p>
              <p className='text-caption text-text-tertiary mt-0.5'>headline above</p>
            </div>
            <div className='rounded-xl bg-surface-neutral/60 px-3 py-2.5'>
              <p className='text-caption text-text-tertiary mb-0.5'>Excluded (eval/other)</p>
              <p className='text-body-sm font-semibold text-text-primary'>
                ${(usageData.excluded_cost_usd ?? 0).toFixed(2)}
              </p>
              <p className='text-caption text-text-tertiary mt-0.5'>env − team</p>
            </div>
          </div>
          {(usageData.env_total_cost_usd ?? 0) > 0 && (
            <div className='mt-3 h-2 rounded-full overflow-hidden bg-surface-neutral flex'>
              <div
                className='h-full bg-accent-purple'
                style={{
                  width: `${Math.min(
                    100,
                    ((usageData.team_total_cost_usd ?? 0) / usageData.env_total_cost_usd) * 100
                  )}%`,
                }}
                title='Team share of env total'
              />
              <div
                className='h-full bg-border-subtle'
                style={{
                  width: `${Math.min(
                    100,
                    ((usageData.excluded_cost_usd ?? 0) / usageData.env_total_cost_usd) * 100
                  )}%`,
                }}
                title='Excluded share of env total'
              />
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* Daily spending stacked bar chart */}
      {!isWarming && (
      <div className='bg-white rounded-2xl border border-border-subtle p-6'>
        <h2 className='text-body-lg font-semibold text-text-primary mb-5'>Daily Spending by Person</h2>
        <TeamUsageTrendChart data={usageData} isLoading={isLoading} error={error} />
      </div>
      )}

      {/* Person table */}
      {!isWarming && (
      <div className='bg-white rounded-2xl border border-border-subtle p-6'>
        <h2 className='text-body-lg font-semibold text-text-primary mb-4'>Per-Person Summary</h2>
        <TeamUsageMonthlyTable data={usageData} isLoading={isLoading} />
      </div>
      )}

      {/* Budget config panel (collapsible) */}
      <div className='bg-white rounded-2xl border border-border-subtle'>
        <button
          onClick={() => setShowBudget((s) => !s)}
          className='w-full flex items-center justify-between p-6 text-left hover:bg-surface-neutral/30 transition-colors rounded-2xl'
        >
          <div className='flex items-center gap-2'>
            <svg className='w-5 h-5 text-text-secondary' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75' />
            </svg>
            <h2 className='text-body-lg font-semibold text-text-primary'>Budget Configuration</h2>
          </div>
          <svg
            className={`w-5 h-5 text-text-tertiary transition-transform ${showBudget ? "rotate-180" : ""}`}
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth={2}
          >
            <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
          </svg>
        </button>
        {showBudget && (
          <div className='px-6 pb-6'>
            <BudgetConfigPanel env={selectedEnv} usageData={isWarming ? null : usageData} />
          </div>
        )}
      </div>
    </div>
  );
}

export default TeamUsage;
